import React, { useState, useEffect } from 'react';
import { apiFetch } from '../jscontrol/apiClient';
import '../css/Category.css';
import ColorValueField, { isSelectedColorAttribute } from '../productattributevalue/ColorValueField';
import { isValidColorValue, normalizeColorValue } from '../../utils/colorUtils';

const BundleProductAttributeValueEdit = ({ editItem, onClose, onSuccess, bundleProductId }) => {
  const [form, setForm] = useState({
    AttributeId: editItem.ProductAttributeId || '',
    AttributeValue: editItem.AttributeValue || '',
    IsActive: editItem.IsActive !== undefined ? editItem.IsActive : true,
  });
  const [attributes, setAttributes] = useState([]);
  const [loadingAttrs, setLoadingAttrs] = useState(true);
  const [attrError, setAttrError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const isColorAttribute = isSelectedColorAttribute(attributes, form.AttributeId);

  useEffect(() => {
    const fetchAttributes = async () => {
      setLoadingAttrs(true);
      setAttrError(null);
      try {
        const { data } = await apiFetch('/ProductAttribute/GetDropdown');
        const items = Array.isArray(data?.data) ? data.data : [];
        setAttributes(items);
      } catch (e) {
        setAttrError(`Could not load attributes: ${e.message}`);
        setAttributes([]);
      } finally {
        setLoadingAttrs(false);
      }
    };
    fetchAttributes();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'AttributeId') {
        next.AttributeValue = '';
      }
      return next;
    });
  };

  const handleColorValueChange = (value) => {
    setForm((prev) => ({ ...prev, AttributeValue: value }));
  };

  const handleSave = async () => {
    if (!form.AttributeId) {
      setFormError('Please select a Product Attribute.');
      return;
    }
    if (!form.AttributeValue.trim()) {
      setFormError('Value is required.');
      return;
    }
    if (isColorAttribute && !isValidColorValue(form.AttributeValue)) {
      setFormError('Please enter a valid color value.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const attributeValue = isColorAttribute
        ? normalizeColorValue(form.AttributeValue)
        : form.AttributeValue.trim();

      await apiFetch('/BundleProductAttributeValue/Update', {
        method: 'PUT',
        data: {
          BundleProductAttributeValueId: editItem.BundleProductAttributeValueId,
          BundleProductId: Number(bundleProductId),
          ProductAttributeId: Number(form.AttributeId),
          AttributeValue: attributeValue,
          UpdatedBy: 0,
        },
      });
      onSuccess('Bundle attribute value updated successfully.');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="cat-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="cat-modal">
        <div className="cat-modal-header">
          <h5>Edit Attribute Value</h5>
          <button className="cat-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="cat-modal-body">
          {formError && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
              <i className="mdi mdi-alert-circle me-1"></i>{formError}
            </div>
          )}

          <div className="form-group mb-3">
            <label className="form-label">
              Product Attribute <span className="text-danger">*</span>
            </label>
            {loadingAttrs ? (
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>Loading attributes...</div>
            ) : attrError ? (
              <div className="alert alert-warning py-2" style={{ fontSize: '0.82rem' }}>{attrError}</div>
            ) : (
              <select
                className="form-control"
                name="AttributeId"
                value={form.AttributeId}
                onChange={handleChange}
              >
                <option value="">— Select Attribute —</option>
                {attributes.map((a) => (
                  <option key={a.ProductAttributeId} value={a.ProductAttributeId}>
                    {a.AttributeName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group mb-3">
            <label className="form-label">
              {isColorAttribute ? 'Color' : 'Value'} <span className="text-danger">*</span>
            </label>
            {isColorAttribute ? (
              <ColorValueField
                value={form.AttributeValue}
                onChange={handleColorValueChange}
                disabled={saving}
              />
            ) : (
              <input
                className="form-control"
                name="AttributeValue"
                value={form.AttributeValue}
                onChange={handleChange}
                placeholder="Enter value"
              />
            )}
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              name="IsActive"
              id="pavEditIsActive"
              checked={form.IsActive}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="pavEditIsActive">Active</label>
          </div>
        </div>

        <div className="cat-modal-footer">
          <button className="btn btn-light me-2" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Saving...
              </>
            ) : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleProductAttributeValueEdit;
